// Angular
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

// SyncFusion
import { Column, ColumnModel, ExcelFilterBase, GridComponent, PredicateModel, row } from '@syncfusion/ej2-angular-grids';

// Cerium
import { CerGridComponent } from './cer-grid.component';
import { GridDesign } from './cer-grid-design';
import { CerGridCommandService } from './cer-grid-command.service';
import { Dialog } from '@syncfusion/ej2-angular-popups';
import { FieldMetadata } from '../cer-data/cer-data-types';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { uiCmdColumnFilter, uiCmdColumnFilterClear, uiCmdFilterByClipboard, uiCmdFilterCellValue, uiCmdFilterCellValueNot, uiCmdFilterClear, uiCmdFilterShow, uiCmdSearchBarFocus, uiCmdSearchClear } from '@ui/cer-ui-commands';

@Injectable()
export class CerGridFilterService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  private grid: GridComponent;

  private searchInputKeyup$: Observable<KeyboardEvent>;
  public filterUrlLatest: string = '';

  // Constructor
  constructor(private commandService: CerGridCommandService, private selectionService: CerGridSelectionService, private ui: CerPromptService) {
    this.subscriptionManager.add(this.commandService.commandPre$.subscribe(cmd => this.onCommandPre(cmd)));
    this.subscriptionManager.add(this.commandService.commandPost$.subscribe(cmd => this.onCommandPost(cmd)));
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public init(grid: GridComponent, design: GridDesign) {
    this.grid = grid;
    if (design?.columnFilterVisible) {
      this.grid.filterSettings = { type: 'Excel' /*, enableInfiniteScrolling: true */ };
    }
    //this.searchInputKeyup$ = fromEvent<KeyboardEvent>(grid.element, 'keyup');
    //this.subscriptionManager.add(
    //  this.searchInputKeyup$.pipe(map((event: KeyboardEvent) => event), debounceTime(1000)).subscribe(e => this.onSearchInputDebounced(e))
    //);
  }

  private onCommandPre(event: UiCommandEvent) {
    if (event.cancel) {
      return;
    }
    switch (event.source) {
      case UiCommandSourceEnum.ActionBegin:

        switch (event.actionType) {
          case UiActionTypeEnum.FilterBeforeOpen:
            this.onFilterBeforeOpen(event.args);
            break;
          case UiActionTypeEnum.FilterChoiceRequest:
            this.onFilterChoiceRequest(event.args);
            break;
          case UiActionTypeEnum.FilterSearchBegin:
            this.onFilterSearchBegin(event.args);
            break;
          case UiActionTypeEnum.Sorting:
            this.onSorting(event.args);
            break;

          case UiActionTypeEnum.Filtering:
            this.onFiltering(event.args);
            break;
          case UiActionTypeEnum.BeforeOpenColumnChooser:
            this.onBeforeOpenColumnChooser(event.args);
            break;
          case UiActionTypeEnum.BeforeCloseColumnChooser:
            this.onBeforeCloseColumnChooser(event.args);
            break;

        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.DataBound:
            this.searchInputAllowClear();
            var url: string = this.filterUrlGet(event);
            if (url != null && url.length > 0) {
              this.filterUrlLatest = url;
            }
            break;
        }
        break;
      case UiCommandSourceEnum.Toolbar:
        {
          switch (event.actionType) {
            case UiActionTypeEnum.ToolbarMenuItem:
              const gridId: string = (<any>this.grid.toolbarModule).gridID;
              if (gridId?.length > 0 && event.commandId == gridId + '_search') {
                this.onSearchClick(event, gridId);
              }
              break;
          }

        }
    }
    switch (event.commandId) {
      case uiCmdColumnFilter.id:
        if (event.source == 'contextMenu') {
          this.commandService.contextMenuClose();
        }
        this.filterColumnMenu(event.columnName);
        break;
      case uiCmdColumnFilterClear.id:
        this.filterColumnClear(event.columnName);
        break;
      case uiCmdFilterClear.id:
        this.filterClear();
        break;
      case uiCmdFilterCellValue.id:
        this.filterByCellValue(event.columnName, event.rowData, 'equal');
        break;
      case uiCmdFilterCellValueNot.id:
        this.filterByCellValue(event.columnName, event.rowData, 'notEqual');
        break;
      case uiCmdFilterByClipboard.id:
        this.filterByClipboard(event.columnName);
        break;
      case uiCmdSearchBarFocus.id:
        this.doSearchBarFocus();
        break;
      case uiCmdSearchClear.id:
        this.doSearchBarClear();
        break;
      case uiCmdFilterShow.id:
        this.doFilterShow();
        break;
    }
  }

  private onSearchClick(event: UiCommandEvent, gridId: string) {
    if (this.grid.enableAdaptiveUI || this.grid.searchModule == null) {
      return;
    }
    const target: HTMLElement = event.args?.originalEvent?.target;
    var targetId: string = target?.id;
    switch (targetId) {
      case gridId + '_searchbar':
        //target.removeAttribute("display");
        break;
      case gridId + '_searchbutton':
        //target.setAttribute("display","none");
        break;
      case gridId + '_clearbutton':
        break;
    }
  }

  private onCommandPost(event: UiCommandEvent) {
    if (event.cancel) {
      return;
    }
    if (event.source == UiCommandSourceEnum.ActionComplete) {
      switch (event.actionType) {
        case UiActionTypeEnum.DataBound:
          if (this.searchBarFocusDeferred) {
            this.searchBarFocusDeferred = false;
            if (event.rowsData && event.rowsData.length > 0) {
              this.searchBarFocusDeferredFromDatabound = true;
            }
            this.doSearchBarFocus();
          }
          break;
        case UiActionTypeEnum.RowSelect:
        case UiActionTypeEnum.CellSelect:
          if (this.searchBarFocusDeferredFromDatabound) {
            this.searchBarFocusDeferredFromDatabound = false;
            this.doSearchBarFocus();
          }
          break;
      }
    }
  }


  // Sorting
  private onSorting(args: any) {
    this.selectionService.rowSelectedSaveFirst();
  }

  // Searching
  private onSearchInputDebounced(e: KeyboardEvent) {
    var id = (<any>e.target).getAttribute('id');
    if (id && id.indexOf('_searchbar') !== - 1) {
      var searchStr: string = (e.target as HTMLInputElement).value;
      this.grid.search(searchStr);
      this.grid.searchSettings.key = searchStr;
      this.searchBarFocusDeferred = true;
    }
  }

  private searchBarFocusDeferred: boolean = false;
  private searchBarFocusDeferredFromDatabound: boolean = false;
  public doSearchBarFocus(): boolean {
    var ok: boolean = false;
    if (this.grid) {
      var searchBar = document.getElementById(this.grid.element.id + "_searchbar");
      if (searchBar) {
        // Open if closed in popup
        var popup: HTMLElement = searchBar.parentElement;
        while (popup && !popup.classList.contains("e-toolbar")) {
          if (popup.classList.contains("e-popup-close")) {
            popup.classList.remove("e-popup-close");
            popup = null;
          }
          else {
            popup = popup.parentElement;
          }
        }
        searchBar.focus();
        ok = true;
      }
    }
    return ok;
  }

  public searchInputAllowClear() {
    var eSearchList = this.grid.element.getElementsByClassName('e-search');
    if (eSearchList && eSearchList.length > 0) {
      var eSearch: Element = eSearchList[0];
      //  checks whether the cancel icon is already present or not
      /*
    if (!eSearch.classList.contains('e-search-can-clear')) {
      var span = document.createElement('span');
      span.id = this.grid.element.id + '_searchcancelbutton';
      span.className = 'e-clear-icon';
      span.title = "Nulstil (Esc)";
      span.addEventListener('click', (args) => {
        var input: HTMLInputElement = this.grid.element.querySelector('.e-search').getElementsByTagName('input')[0];
        if (input && input.value != "") {
          input.value = "";
          this.grid.search("");
        }
      });
      eSearch.appendChild(span);
      eSearch.classList.add('e-search-can-clear');
      eSearch.classList.add('e-search-small');
    }
      */
    }
  }

  public doSearchBarClear(): boolean {
    var ok: boolean = false;
    if (this.grid && this.grid.searchSettings.key !== "") {
      this.grid.searchSettings.key = "";
      ok = true;
    }
    return ok;
  }

  // Filtering

  public filterColumnClassRemove() {
    var fltrIconElements = this.grid?.element?.getElementsByClassName('e-filtered');
    if (fltrIconElements != null) {
      for (var i = fltrIconElements.length - 1; i >= 0; i--) {
        const ele = fltrIconElements[i];
        if (ele != null) {
          ele.removeAttribute('aria-filtered');
          ele.classList.remove('e-filtered');
        }
      }
    }
  }

  private onFiltering(args: any) {
    this.selectionService.rowSelectedSaveFirst();
  }

  public filterColumnMenu(columnName: string) {
    if (columnName?.length > 0) {
      var column = this.grid.getColumnByField(columnName);
      var header = this.grid.getColumnHeaderByField(columnName);
      var target = header.querySelector('.e-filtermenudiv');
      if (!target) {
        return;
      }
      var gClient = this.grid.element.getBoundingClientRect();
      var fClient = /*target*/ header.getBoundingClientRect();
      (<any>this.grid.filterModule).filterDialogOpen(column, target, fClient.right - gClient.left, fClient.bottom - gClient.top);
    }
  }

  public filterColumnClear(columnName: string) {
    if (columnName?.length > 0) {
      this.grid.clearFiltering([columnName]);
    }
  }

  public filterClear() {
    this.grid.filterSettings.columns = [];
    //this.grid.filterModule.clearFiltering();
    this.doSearchBarClear()
    this.grid.clearGrouping();
    this.grid.clearSorting();
  }

  private filterUrlGet(event: UiCommandEvent): string {
    if (event.appComponent != null && event.appComponent instanceof CerGridComponent) {
      var dm: any = event.appComponent.grid?.renderModule?.data?.dataManager;
      if (dm?.requests != null && dm.requests.length > 0) {
        return dm.requests[0].url;
      }
    }
    return null;
  }

  public doFilterShow() {
    var s: string[] = [''];
    if (this.filterUrlLatest != null && this.filterUrlLatest.length > 0) {
      s = this.filterUrlLatest.split(/(\+*?[?&])/);
    }
    this.ui.info(s, "Forespørgsel");
  }

  private clipboardGetValues(): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      navigator.clipboard.readText()
        .then(data => {
          var values: string[] = data.split('\r\n');
          values = values.filter(value => value?.length > 0);
          resolve(values)
        })
        .catch(error => reject())
    });
  }


  public filterByClipboard(columnName: string) {

    var col = this.grid.getColumnByField(columnName);
    if (col) {
      var field = CerGridFilterService.isForeignColumnReplacement(col) ? col.foreignKeyValue : col.field;
      var urlLenEstimate = 100; // baseUrl estimate
      const urlLenMax = 8000; // baseUrl estimate
      var completed = false;
      this.clipboardGetValues().then(values => {
        if (values?.length > 0) {
          var existingFilter: PredicateModel[] = this.grid.filterSettings.columns.filter((c: PredicateModel) => c.uid != col.uid);
          values.forEach(value => {
            if (!completed) {
              //var value  = this.resolveFieldValue(col, valueFK);
              var newFilterPredicate: PredicateModel = { field: field, uid: col.uid, operator: 'equal', value: value, isForeignKey: col.isForeignColumn(), predicate: 'or' };
              urlLenEstimate += 34 + field.length + value.length;
              if (urlLenEstimate > urlLenMax) {
                this.ui.error(`Filter antal overskrider begrænsning. ${existingFilter.length} værdier filtreret`);
                completed = true;
              }
              if (!completed) {
                existingFilter = existingFilter.concat(newFilterPredicate);
              }
            }
          })
          this.grid.filterSettings.columns = existingFilter;
          //this.grid.filterByColumn(columnName, 'equal', value);
        }
      });
    }
  }

  public filterByCellValue(columnName: string, rowData: any, operator: string = 'equal') {
    if (rowData) {
      var value = rowData[columnName];
      if (value != undefined) {
        var col = this.grid.getColumnByField(columnName);
        if (col) {
          var field =/* col.isForeignColumn() ? col.foreignKeyValue :*/ col.field;
          //value = this.resolveFieldValue(col, value);
          var existingFilter: PredicateModel[] = this.grid.filterSettings.columns.filter(c => c.uid != col.uid);
          var newFilterPredicate: PredicateModel = { field: field, uid: col.uid, operator: operator, value: value, isForeignKey: col.isForeignColumn(), predicate: 'and' };
          this.grid.filterSettings.columns = existingFilter.concat(newFilterPredicate);
          //this.grid.filterByColumn(columnName, 'equal', value);
        }
      }
    }
  }

  public static resolveFieldValue(col: Column, value: any): any {
    var resolvedValue = value;
    if (col && CerGridFilterService.isForeignColumnReplacement(col)) {
      // If foreign key column, we need to filter by the foreign key value
      resolvedValue = CerGridFilterService.foreignKeyValue(col, value);
    }
    return resolvedValue;
  }

  public getForeignKeyValue(col: Column, fkValue: any): any {
    return CerGridFilterService.foreignKeyValue(col, fkValue);
  }

  public static foreignKeyValue(col: Column, fkValue: any): any {
    var value: any = null;
    var dm: any = col.dataSource;
    var fkData: any = dm?.dataSource?.json;
    if (fkData && Array.isArray(fkData)) {
      var fk = fkData.filter((fk: any) => fk[col.foreignKeyField] == fkValue);
      if (fk?.length > 0) {
        value = fk[0][col.foreignKeyValue];
      }
    }
    return value;
  }

  private onFilterSearchBegin(args: any) {
    this.filterSettingsSetup(args);
  }

  private onFilterBeforeOpen(args: any) {
    if (args.filterModel && args.filterModel instanceof ExcelFilterBase) {
      //this.filterDialogSetup(args.filterModel);
    }
  }

  private onFilterChoiceRequest(args: any) {
    if (args.filterModel && args.filterModel instanceof ExcelFilterBase) {
      this.filterSettingsSetup(args);
      this.filterDialogSetup(args.filterModel);
    }
  }

  private filterDialogSetup(filterModel: ExcelFilterBase) {
    if (filterModel && filterModel.options) {
      var d: HTMLElement = (<any>filterModel)?.dlg;
      if (d) {
        var gridEle: HTMLElement = this.grid.element;
        var gridRect: DOMRect = gridEle.getBoundingClientRect();
        var docRect: DOMRect = document.documentElement.getBoundingClientRect();

        var top = Math.trunc(filterModel.options.position?.Y + gridRect.top);
        var left = Math.trunc(filterModel.options.position?.X + gridRect.left);
        var width = 300;
        var height = 1000;

        if (left + width > docRect.width) {
          left = docRect.width - width;
          if (left < 0) {
            left = 0;
            width = Math.min(docRect.width);
          }
        }
        if (top + height > docRect.height) {
          height = docRect.height - top;
          if (height < 500) {
            height = 500;
            top = Math.max(docRect.height - height, 0);
            if (top == 0) {
              height = Math.min(docRect.height, height);
            }
          }
        }

        d.style.position = "fixed";
        d.style.top = top + "px";
        d.style.left = left + "px";
        d.style.height = height + "px";
        d.style.width = width + "px";
        d.style.maxHeight = "calc(100vh - " + top + "px)";

        (<any>filterModel).options.height = height;
      }
    }
  }

  private filterSettingsSetup(args: any) {
    if (args) {
      args.filterChoiceCount = 5000;  // TODO: Take from environment
      var filterfields = [];
      var objFilter = Object.keys(args.filterModel.existingPredicate);
      for (var i = 0; i < objFilter.length; i++) {
        filterfields.push(objFilter[i]);
      }
      filterfields.push(args.filterModel.options.field);
      args.query.distincts = [];

      var filterModel = args.filterModel;
      if (filterModel) {
        var col = this.grid.getColumnByField(args.filterModel.options.field);
        filterModel.isForeignColumn = CerGridFilterService.isForeignColumnReplacement; // redirect filterModel.isForeignColumn to handle non replacement foreign key columns;
        if (col != null) {
          (col as any).isForeignColumn = CerGridFilterService.isForeignColumnReplacement; // redirect filterModel.isForeignColumn to handle non replacement foreign key columns;
        }

        if (filterModel.options.field.length > 0) {
          if (col && !filterModel.isForeignColumn(col)) {
            args.query.select(filterfields); // Created the select query
          }
        }
      }
    }
  }

  public static isForeignColumnReplacement(col: any): boolean {
    var isFK: boolean = col?.isForeignColumn ? col.isForeignColumn() : false;
    if (isFK) {
      isFK = (col.foreignKeyField != col.foreignKeyValue);
    }
    return isFK;
  }

  // Filter
  public getFieldFilterValueNumber(fieldName: string): number {

    var predicate = this.grid.filterSettings.columns.find(item => item.field == fieldName);
    if (predicate && predicate.operator == 'equal') {
      return <number>predicate.value;
    }
    return null;
  }

  public filterColumnSetOnClient(column: ColumnModel, meta: FieldMetadata, predicate = 'and') {
    if (meta.filterOperator && meta.filterValue != undefined && meta.filterOnClient == true) {
      //{ field: 'OrderDate', matchCase: false, operator: 'equal', predicate: 'or', value: new Date() }]
      if (meta.filterOperator == 'in') {
        if (meta.filterValue.length && meta.filterValue.length > 0) {
          var valueArray: any[] = meta.filterValue;
          valueArray.forEach(v => {
            var metaIn: FieldMetadata = Object.assign({}, meta);
            metaIn.filterOperator = 'equal';
            metaIn.filterValue = v;
            this.filterColumnSetOnClient(column, metaIn, 'or');
          });
        }
      }
      else {
        var p: PredicateModel =
        {
          field: meta.name,
          operator: meta.filterOperator,
          value: meta.filterValue,
          predicate: predicate
        };
        if (this.grid?.filterSettings) {
          if (this.grid.filterSettings.columns.length == 0) {
            this.grid.filterSettings.columns = [p];
          }
          else {
            this.grid.filterSettings.columns.push(p);
          }
        }
      }
    }
  }

  /*****************
   * Column chooser
   *****************/

  private onBeforeOpenColumnChooser(args: any) {
    if (this.grid?.columnChooserModule) {
      var chooser: any = this.grid.columnChooserModule;
      if (chooser.dlgObj) {
        var dlg: Dialog = chooser.dlgObj;
        if (dlg) {
          dlg.allowDragging = false;
          dlg.enableResize = false;
          var dlgObj: any = dlg;
          dlgObj.allowMaxHeight = true; // TODO:? ?
          this.columnChooserDlgObjInit(dlgObj, args);
        }
      }
    }
  }

  private onBeforeCloseColumnChooser(args: any) {
  }

  private columnChooserDlgObjInit(dlgObj: any, args: any) {

    if (!(<any>this.grid.columnChooserModule).customDialogOpen_orig) {
      (<any>this.grid.columnChooserModule).customDialogOpen_orig = (<any>this.grid.columnChooserModule).customDialogOpen;
      (<any>this.grid.columnChooserModule).customDialogOpen = this.onBeforeOpenDlgColumnChooserDialog.bind(this);
      dlgObj.beforeClose = this.onBeforeCloseDlgColumnChooser.bind(this);
    }
  }

  private onBeforeOpenDlgColumnChooserDialog(args: any) {
    var dlg: any = args.container;
    if (dlg) {
      dlg.allowDragging = false;
      dlg.enableResize = false;
      dlg.isModal = true;
      dlg.style.width = "300px";
      dlg.style.top = "0px";
      dlg.style.left = "calc(100vw - 300px)";
      dlg.style.position = "fixed";
      dlg.style.height = "100vh";
      args.maxHeight = "100vh";
    }
    (<any>this.grid.columnChooserModule).customDialogOpen_orig(args);
  }

  private onBeforeCloseDlgColumnChooser(args: any) {
    console.log(args);
  }
}